import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { SlOptions } from "react-icons/sl";
import { MdFavorite } from "react-icons/md";
import OptionsList from "../OffCanvasComponents/OptionsList";
import { mySetterConversationsInbox } from "./off-canvas-list-messages";
import { getCachedList, cacheList } from "../../services/messageCache";
import { DisplayDiscussionForMiniScreenContext } from "../../contexts/DisplayDiscussionForMiniScreenContext";
import { AuthContext } from "../../contexts/AuthContext";
import { WorkspaceContext } from "../../contexts/WorkspaceContext";
import { joinRoomInbox } from "../../services/conversations";
import { stripAccents } from "../../utils/stripAccents";
import Person from "../Person";
import { socket } from "../../services/socket";
import { timeElapsed } from "../../services/getTimeSended";
import { CritereFiltreContext } from "../../contexts/CriteresFiltreContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { includesId } from "../../services/idUtils";

export default function OffCanvasMessages() {
  const {
    setDisplayDiscussionForMiniScreen,
    setDestinataire,
    setActiveRoomIdInbox,
    typingInboxMap,
  } = useContext(DisplayDiscussionForMiniScreenContext);
  const { user } = useContext(AuthContext);
  const { currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;
  const { critereFiltre, searchText } = useContext(CritereFiltreContext);

  const [visibleIndex, setVisibleIndex] = useState(null);
  const [conversations, setConversations] = useState([]);
  const { theme } = useContext(ThemeContext);

  const backgroundColor = useMemo(
    () => (theme === "dark" ? "#504c4ca8" : "#0067ff38"),
    [theme]
  );

  const toggleOptionsList = (idx) => {
    setVisibleIndex(visibleIndex === idx ? null : idx);
  };

  // Filtre calculé automatiquement à chaque changement de conversations ou critère
  const conversationsParFiltre = useMemo(() => {
    let filtered;
    if (critereFiltre === "Tous les messages") {
      filtered = conversations.filter((dis) => dis.conversation === "inbox");
    } else if (critereFiltre === "Messages non lus") {
      filtered = conversations.filter(
        (dis) =>
          !includesId(dis.readBy, user.userId) &&
          dis.lastMessage?.destinataire === user.userId
      );
    } else {
      filtered = conversations.filter((dis) =>
        includesId(dis.addFavoriteBy, user.userId)
      );
    }
    if (searchText) {
      const term = stripAccents(searchText);
      filtered = filtered.filter((dis) => {
        const inter = dis.interlocuteur || {};
        return (
          stripAccents(inter.prenom).includes(term) ||
          stripAccents(inter.nom).includes(term) ||
          stripAccents(inter.username).includes(term)
        );
      });
    }
    return filtered;
  }, [conversations, critereFiltre, searchText, user.userId]);

  // Chargement initial : cache d'abord, puis serveur
  useEffect(() => {
    getCachedList(`conversations_inbox_${workspaceId}`).then((cached) => {
      if (cached) setConversations(cached);
    });
    socket.emit("ma_liste_des_discussions_inbox", user.userId);
  }, [workspaceId]);

  // Mise à jour du offcanvas après suppression d'un message
  const handleDeleteUpdate = useCallback(({ roomId, lastMessage }) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.roomId === roomId
          ? { ...conv, lastMessage: lastMessage || {} }
          : conv
      )
    );
  }, []);

  // Mise à jour locale des favoris
  const handleFavoriteUpdate = useCallback(
    ({ roomId, added }) => {
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.roomId !== roomId) return conv;
          const favs = conv.addFavoriteBy || [];
          return {
            ...conv,
            addFavoriteBy: added
              ? [...favs, user.userId]
              : favs.filter((id) => id !== user.userId),
          };
        })
      );
    },
    [user.userId]
  );

  useEffect(() => {
    socket.on(
      "message_inbox_ouvert_par_le_destinataire_dans_le_off_canvas",
      (canalUpdated) => {
        setConversations((prev) => {
          const filtered = prev.filter(
            (canal) => canal._id !== canalUpdated._id
          );
          return [...filtered, canalUpdated].sort(
            (a, b) => (b.lastMessage?.timeUnix || 0) - (a.lastMessage?.timeUnix || 0)
          );
        });
      }
    );

    socket.on(
      "mettre_a_jour_le_dernier_message_inbox_sur_le_offcanvas",
      ({ canal }) => {
        if (!canal) return;
        setConversations((prev) => {
          const interlocuteur = (canal.membres || []).find(
            (m) => m.userId !== user.userId
          );
          const updated = {
            _id: canal.canalId || canal._id,
            membres: canal.membres,
            roomId: canal.roomId,
            lastMessage: canal.lastMessage,
            readBy: canal.readBy || [],
            addFavoriteBy: canal.addFavoriteBy || [],
            interlocuteur: interlocuteur || {},
            conversation: canal.conversation || "inbox",
          };
          const filtered = prev.filter((c) => c.roomId !== canal.roomId);
          return [...filtered, updated]
            .filter((c) => c.lastMessage?.contenu != null)
            .sort((a, b) => (b.lastMessage?.timeUnix || 0) - (a.lastMessage?.timeUnix || 0));
        });
      }
    );

    socket.on("voici_ma_liste_de_conversation_inbox", (convs) => {
      mySetterConversationsInbox(convs, (sorted) => {
        setConversations(sorted);
        cacheList(`conversations_inbox_${workspaceId}`, sorted);
      });
    });

    socket.on("dernier_message_apres_suppression", handleDeleteUpdate);

    socket.on("channel_added_with_success", handleFavoriteUpdate);
    socket.on("channel_removed_with_success", handleFavoriteUpdate);

    return () => {
      socket.off("message_inbox_ouvert_par_le_destinataire_dans_le_off_canvas");
      socket.off("mettre_a_jour_le_dernier_message_inbox_sur_le_offcanvas");
      socket.off("voici_ma_liste_de_conversation_inbox");
      socket.off("dernier_message_apres_suppression", handleDeleteUpdate);
      socket.off("channel_added_with_success", handleFavoriteUpdate);
      socket.off("channel_removed_with_success", handleFavoriteUpdate);
    };
  }, [user.userId, handleFavoriteUpdate, handleDeleteUpdate, workspaceId]);

  return (
    <>
      <ul className="tyn-aside-list">
        {conversationsParFiltre.map((inter, idx) => (
          <li
            className={`tyn-aside-item js-toggle-main ${
              inter.lastMessage?.emetteur === user.userId ||
              inter.readBy.length === 2
                ? ""
                : "unread"
            } `}
            key={inter.roomId}
            style={{
              backgroundColor:
                inter.readBy.length === 1 &&
                inter.lastMessage?.emetteur !== user.userId
                  ? backgroundColor
                  : null,
            }}
          >
            <div className="tyn-media-group">
              <div className="tyn-media tyn-size-lg">
                <Person
                  person={{
                    image: inter.interlocuteur.userImage,
                    nom: inter.interlocuteur.nom,
                    prenom: inter.interlocuteur.prenom,
                    userId: inter.interlocuteur.userId,
                    colors: inter.interlocuteur.color,
                  }}
                />
              </div>
              <div
                className="tyn-media-col"
                onClick={() => {
                  joinRoomInbox(user, inter.interlocuteur, workspaceId);
                  setDisplayDiscussionForMiniScreen(true);
                  setDestinataire(inter.interlocuteur);
                  setActiveRoomIdInbox(
                    user.userId > inter.interlocuteur.userId
                      ? `${user.userId}_${inter.interlocuteur.userId}`
                      : `${inter.interlocuteur.userId}_${user.userId}`
                  );
                  socket.emit(
                    "je_declare_tous_les_messages_inbox_de_cette_discussion_ouvert",
                    {
                      destinataire: user.userId,
                      roomId:
                        user.userId > inter.interlocuteur.userId
                          ? `${user.userId}_${inter.interlocuteur.userId}`
                          : `${inter.interlocuteur.userId}_${user.userId}`,
                      workspaceId,
                    }
                  );
                  socket.emit(
                    "vaChercherLaConversation",
                    user.userId > inter.interlocuteur.userId
                      ? `${user.userId}_${inter.interlocuteur.userId}`
                      : `${inter.interlocuteur.userId}_${user.userId}`,
                    workspaceId
                  );
                }}
              >
                <div className="tyn-media-row">
                  <h6 className="name">
                    {inter.interlocuteur.username}
                  </h6>
                  {includesId(inter.addFavoriteBy, user.userId) && (
                    <MdFavorite
                      className="text-danger"
                      style={{ fontSize: "12px", marginLeft: "4px", flexShrink: 0 }}
                    />
                  )}
                  {Object.values(typingInboxMap).includes(inter.interlocuteur.userId) && (
                    <span className="typing text-primary my-text-writting-alternate-infinite">
                      Écrit...
                    </span>
                  )}
                </div>
                <div className="tyn-media-row has-dot-sap">
                  <p className="content">
                    {inter.lastMessage?.nature === "code_meeting"
                      ? "Invitation reunion"
                      : inter.lastMessage?.contenu}
                  </p>
                  {inter.lastMessage?.contenu ? (
                    <span className="meta text-primary">
                      {timeElapsed(inter.lastMessage.timeUnix)}
                    </span>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              <div className="tyn-media-option tyn-aside-item-option">
                <ul className="tyn-media-option-list">
                  <li className="dropdown">
                    <div
                      className="btn btn-icon btn-white btn-pill dropdown-toggle"
                      data-bs-toggle="dropdown"
                      data-bs-offset="0,0"
                      data-bs-auto-close="outside"
                      onClick={() => {
                        toggleOptionsList(idx);
                      }}
                    >
                      <SlOptions style={{ cursor: "pointer" }} />
                    </div>
                    {visibleIndex === idx && (
                      <OptionsList
                        roomId={inter.roomId}
                        addFavoriteBy={inter.addFavoriteBy}
                        setDisplayDiscussionForMiniScreen={
                          setDisplayDiscussionForMiniScreen
                        }
                        interlocuteur={inter.interlocuteur}
                        display={true}
                      />
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
